/**
 * Pure, server-safe resolver for route-declared `<title>` / meta description.
 * Used by both the browser (`RouteMeta` component) and the Express host
 * (`host/src/meta.ts`). Must stay DOM-free and React-free.
 */
import {matchPath} from 'react-router';

import {
  ROUTE_META_REGISTRY,
  type RouteMeta,
  substituteMeta,
} from './routeMetaRegistry';

export type ResolvedRouteMeta = {
  meta: RouteMeta;
  params: Record<string, string>;
};

export function resolveRouteMeta(
  pathname: string
): ResolvedRouteMeta | undefined {
  for (const entry of ROUTE_META_REGISTRY) {
    const match = matchPath<Record<string, string>>(pathname, {
      path: entry.path,
      exact: entry.exact,
    });
    if (match) {
      return {meta: entry.meta, params: match.params ?? {}};
    }
  }
  return undefined;
}

/**
 * Resolves declared route meta for a pathname and substitutes placeholders
 * using `meta.defaultVars` plus any values returned by `meta.resolveVars`.
 * Returns `undefined` if no route in the registry matches.
 */
export function resolveAndSubstituteRouteMeta(
  pathname: string
): {title: string; description: string} | undefined {
  const resolved = resolveRouteMeta(pathname);
  if (!resolved) {
    return undefined;
  }
  const {meta, params} = resolved;
  const dynamicVars = meta.resolveVars ? meta.resolveVars(params) : undefined;
  return substituteMeta(meta, dynamicVars);
}
